import { Injectable } from "@angular/core";
import {
  PatientPlanService,
  PatientPlanFavouriteSaveRequest,
  PatientPlanDetailResponse,
  CardModel,
  CardSessionCardDto,
  CardSessionDto,
  PatientPlanDto,
  PatientPlanFavouriteSaveRequestType,
  ReusePlanRequest,
} from "../../../swagger/api.client.generated";
import { DateHelper } from "../../core/helpers/date.helper";
import * as moment from "moment";
import { HomeService } from "../home/home.service";

@Injectable({
  providedIn: "root",
})
export class PlanService {
  constructor(
    private patientPlanService: PatientPlanService,
    private homeService: HomeService
  ) {}

  savePatientPlanFavouriteApi(saveRequest: PatientPlanFavouriteSaveRequest) {
    return this.patientPlanService.savePatientPlanFavourite(saveRequest);
  }

  reusePlan(request: ReusePlanRequest) {
    return this.patientPlanService.reusePlan(request);
  }

  checkIfPlanExistsOnSameDate(startDate: Date): boolean {
    // 12:00:00 AM
    const startOfDay = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      0,
      0,
      0
    ); // 11:59:59 PM
    const endOfDay = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      23,
      59,
      59
    ); // 11:59 PM

    // use plans in home.service to check if a plan already exists on the startDate provided
    const patientPlans: PatientPlanDto[] = this.homeService.getAllPatientPlans;
    const planFound = patientPlans.find((p: PatientPlanDto) => {
      const planDate = new Date(p.startDate);
      return planDate >= startOfDay && planDate <= endOfDay;
    });
    return !!planFound;
  }

  /**
   * Gets the planId for today's plan if it exists
   * @returns The patientPlanId for today's plan, or null if no plan exists for today
   */
  getTodayPlanId(): number | null {
    // First try to use the current plan from HomeService (which is already filtered for today)
    const currentPlan = this.homeService.getCurrentPlan;
    if (currentPlan && currentPlan.patientPlanId) {
      return currentPlan.patientPlanId;
    }

    // Fallback: search through all plans to find today's plan
    const today = new Date();
    const startOfDay = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      0,
      0,
      0
    ); // 12:00 AM
    const endOfDay = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59
    ); // 11:59 PM

    const patientPlans: PatientPlanDto[] = this.homeService.getAllPatientPlans;
    const planFound = patientPlans.find((p: PatientPlanDto) => {
      const planDate = new Date(p.startDate);
      return planDate >= startOfDay && planDate <= endOfDay;
    });

    return planFound?.patientPlanId ?? null;
  }

  /**
   * Checks if a plan exists for today
   * @returns True if a plan exists for today, false otherwise
   */
  checkIfPlanExistsToday(): boolean {
    return this.getTodayPlanId() !== null;
  }

  // TODO: Remove after GetPatientPlan updated to return PatientPlanDto
  formatToSaveFavourite(
    patientPlan: PatientPlanDetailResponse,
    planName: string
  ): PatientPlanFavouriteSaveRequest {
    // Get timezone-safe local date using helper
    const localDate = DateHelper.getTodayLocal();

    return new PatientPlanFavouriteSaveRequest({
      patientPlanId: patientPlan.patientPlanId,
      patientId: 0, // TODO: get patient id
      planName: planName,
      type: PatientPlanFavouriteSaveRequestType.New,
      patientPlan: new PatientPlanDto({
        patientPlanId: patientPlan.patientPlanId,
        startDate: localDate,
        insertLocalDate: localDate,
        insertedByUserId: 0, // Default value since not available in PatientPlanDetailResponse
        updatedByUserId: 0, // Default value since not available in PatientPlanDetailResponse
        cardSessions:
          patientPlan.planDailyPeriods?.reduce(
            (acc, period) =>
              acc.concat(
                period.cardSessions?.map((session) => {
                  // Collect all cards from the session's cardSections
                  const allCards: CardModel[] = [];

                  // Add angel cards before (if any)
                  if (session.cardSections?.angelCardsBefore) {
                    allCards.push(...session.cardSections.angelCardsBefore);
                  }

                  // Add activity card (if any)
                  if (session.cardSections?.activityCard) {
                    allCards.push(...session.cardSections.activityCard);
                  }

                  // Add angel cards after (if any)
                  if (session.cardSections?.angelCards) {
                    allCards.push(...session.cardSections.angelCards);
                  }

                  // Convert cards to CardSessionCardDto
                  const cardSessionCards = allCards.map(
                    (card, index) =>
                      new CardSessionCardDto({
                        cardSessionCardId: card.cardSessionCardId,
                        cardSessionId: session.cardSessionId,
                        cardId: card.cardId,
                        cardName: card.cardName,
                        cardDescription: card.cardDescription,
                        periodMinutes: card.periodMinutes || 0,
                        sortOrder: card.sortOrder || index,
                        isComplete: card.isComplete || false,
                        isActive: true,
                        cardCategoryId: card.cardCategory?.cardCategoryId,
                        cardCategoryName: card.cardCategory?.cardCategoryName,
                        cardDeckId: card.cardDeckId,
                        cardDeckName: card.cardDeckType?.cardDeckName,
                        htmlScript: card.htmlScript,
                        isMandatory: card.isMandatory || false,
                      })
                  );

                  return new CardSessionDto({
                    cardSessionId: session.cardSessionId,
                    patientPlanId: patientPlan.patientPlanId,
                    dailyPeriodId: period.dailyPeriodId,
                    dailyPeriodName: period.dailyPeriod,
                    sortOrder: session.sessionSortOrder || 0,
                    isActive: true,
                    cardSessionCards: cardSessionCards,
                  });
                }) || []
              ),
            []
          ) || [],
      }),
    });
  }
}
